import { Button, SimpleGrid, Stack, Tag, useDisclosure, Wrap, WrapItem } from '@chakra-ui/react';
import { SurchargeCategory } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { prettyCurrency, prettyDate } from '@pelicargo/utils';
import { useMemo } from 'react';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi2';

import { useAirportTimeZone } from '../../hooks/airport/useAirportTimeZone';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { QuoteLineItem } from '../../utils/types';
import { LabeledText } from '../LabeledText';

type RateDetailProps = {
  lineItem: QuoteLineItem;
};

export const RateDetail = ({ lineItem }: RateDetailProps) => {
  const { request } = useCurrentRequest();
  const { isOpen, onToggle } = useDisclosure();
  const rate = useMemo(() => lineItem?.issued_by_rate, [lineItem?.issued_by_rate]);
  const { data: originAirport } = useAirportTimeZone(rate?.origin_airport_id);

  const actualCw = useMemo(() => {
    return request?.chargeable_weight || 0;
  }, [request?.chargeable_weight]);

  const isConnected = useMemo(() => {
    return lineItem?.disassociated_at === null;
  }, [lineItem?.disassociated_at]);

  const surchargeCategories = useMemo(() => {
    return rate?.surcharge_categories || [];
  }, [rate?.surcharge_categories]);

  const rateCosts = useMemo(() => {
    return rate?.costs || [];
  }, [rate?.costs]);

  const renderSurchargeCategory = (category: SurchargeCategory) => (
    <WrapItem key={category}>
      <Tag key={category} minW="fit-content" size="sm">
        {category}
      </Tag>
    </WrapItem>
  );

  const renderRateCost = (cost: any) => {
    const label = cost?.min ? `${cost?.min} (kg)` : 'N';
    const max = cost?.max || Number.MAX_SAFE_INTEGER;
    const isPossible = actualCw < max;
    const isSelected = cost.id === lineItem?.issued_by_tiered_cost_id;

    const opacity = isSelected || isPossible ? 1 : 0.5;
    const possibilityColor = isPossible ? 'success' : 'error';
    const color = isSelected ? possibilityColor : undefined;

    return (
      <LabeledText key={cost.id} label={label} value={prettyCurrency(cost.cost)} color={color} opacity={opacity} />
    );
  };

  if (!lineItem?.issued_by_rate_id) return null;

  return (
    <>
      <Stack direction="column" spacing="2" pb={3}>
        <Stack direction="column" spacing={0}>
          <Stack direction="column" spacing={3} p={3} flex={1}>
            <SimpleGrid columns={3} rowGap={3} columnGap={2} flex={1}>
              <LabeledText
                flex={1}
                label="Status"
                value={isConnected ? 'Connected' : 'No Connection'}
                color={isConnected ? 'success' : 'error'}
              />
              <LabeledText
                flex={1}
                label="Starts At"
                value={prettyDate(rate?.starts_at, {
                  showTime: false,
                  timeZone: originAirport?.time_zone,
                })}
              />
              <LabeledText
                flex={1}
                label="Expires At"
                value={prettyDate(rate?.expires_at, {
                  timeZone: originAirport?.time_zone,
                })}
              />
            </SimpleGrid>
            <LabeledText label="Comments" value={rate?.comments} />
          </Stack>
          <Stack direction="column" spacing={3} p={3} flex={1} borderTopWidth={1}>
            <SimpleGrid columns={3} rowGap={3} columnGap={2}>
              <LabeledText flex={1} label="Min Cost" value={prettyCurrency(rate?.min_cost)} />
              {rateCosts?.map(renderRateCost)}
            </SimpleGrid>
            <Wrap>{surchargeCategories.map(renderSurchargeCategory)}</Wrap>
          </Stack>

          <Show if={isOpen}>
            <SimpleGrid columns={3} rowGap={3} columnGap={2} p={3} borderTopWidth={1}>
              <LabeledText label="ID" value={String(rate?.id || '')} />
              <LabeledText label="Account ID" value={String(rate?.account_id || '')} />
              <LabeledText label="Product" value={rate?.airline_product} />
              <LabeledText label="Service" value={rate?.airline_service} />
              <LabeledText label="Cargo Type" value={rate?.cargo_type} />
              <LabeledText label="Service Type" value={rate?.service_type} />
              <LabeledText label="Category" value={rate?.rate_category} />
              <LabeledText label="Classes" value={rate?.classes?.join?.(', ')} />
              <LabeledText label="Import" value={String(rate?.import_id)} />
            </SimpleGrid>
          </Show>
        </Stack>
        <Button
          size="xs"
          variant="ghost"
          onClick={onToggle}
          colorScheme="brand"
          alignSelf="center"
          rightIcon={isOpen ? <HiChevronUp /> : <HiChevronDown />}
        >
          {isOpen ? 'Minimize' : 'Expand'}
        </Button>
      </Stack>
    </>
  );
};
